import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getAccessToken } from '../services/api';

const RealtimeContext = createContext(null);

const MAX_BACKOFF_MS = 30_000;

/**
 * Connexion WebSocket temps réel (issue #36).
 * Se connecte à /ws?token=... quand l'utilisateur est authentifié,
 * se reconnecte automatiquement (backoff exponentiel), et dispatche
 * les événements `{ event, data }` aux abonnés via subscribe().
 */
export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Map()); // event -> Set<handler>
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);

  const dispatch = useCallback((event, data) => {
    listenersRef.current.get(event)?.forEach((fn) => {
      try { fn(data); } catch { /* handler isolé */ }
    });
    // abonnés "wildcard" reçoivent (event, data)
    listenersRef.current.get('*')?.forEach((fn) => {
      try { fn(event, data); } catch { /* handler isolé */ }
    });
  }, []);

  const subscribe = useCallback((event, handler) => {
    let set = listenersRef.current.get(event);
    if (!set) {
      set = new Set();
      listenersRef.current.set(event, set);
    }
    set.add(handler);
    return () => set.delete(handler);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let closedByUs = false;

    function connect() {
      const token = getAccessToken();
      if (!token) return;

      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${window.location.host}/ws?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
      };
      ws.onmessage = (e) => {
        try {
          const { event, data } = JSON.parse(e.data);
          dispatch(event, data);
        } catch { /* frame ignorée */ }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (closedByUs) return;
        const delay = Math.min(1000 * 2 ** attemptsRef.current, MAX_BACKOFF_MS);
        attemptsRef.current += 1;
        reconnectRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      closedByUs = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, dispatch]);

  return <RealtimeContext.Provider value={{ subscribe }}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

/**
 * S'abonne à un événement temps réel pour la durée de vie du composant.
 * `event` peut être '*' pour tout recevoir (handler reçoit alors (event, data)).
 */
export function useRealtimeEvent(event, handler) {
  const ctx = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.subscribe(event, (...args) => handlerRef.current?.(...args));
  }, [ctx, event]);
}

export default RealtimeContext;
